import { Form, Image, notification } from "antd";
import { PlusOutlined, FileImageOutlined } from "@ant-design/icons";
import { useCallback, useEffect, useRef } from "react";
import { IoCameraOutline, IoTrashOutline } from "react-icons/io5";
import { Styles } from "./styled";

const MAX_FILES_PER_PICK = 10;
const MAX_BYTES_PER_FILE = 10 * 1024 * 1024;
const ACCEPT_ATTR = "image/png,image/jpeg,.jpg,.jpeg";

const MSG_FILE_SIZE_EXCEEDED =
  "ファイルサイズが上限を超えています。10MB以下のファイルを選択してください。";
const MSG_FILE_COUNT_EXCEEDED =
  "ファイル数が上限を超えています。10件以下のファイルを選択してください。";

const ALLOWED_MIME = new Set(["image/png", "image/jpeg", "image/jpg"]);

interface IProps {
  logId: string;
  readonlyAttachments: unknown[];
  editable?: boolean;
}

const resolveUrl = (item: unknown): string | undefined => {
  if (item == null) return undefined;
  if (typeof item === "string") return item;
  if (typeof item === "object" && item !== null) {
    const o = item as Record<string, unknown>;
    if (typeof o.file_url === "string" && o.file_url) {
      const path = o.file_url;
      if (/^https?:\/\//i.test(path)) return path;
      const base = process.env.REACT_APP_API_URL?.split("/api/v1")?.[0] ?? "";
      return `${base}${path}`;
    }
    if (typeof o.url === "string" && o.url) return o.url;
  }
  return undefined;
};

const filePreviewKey = (file: File) =>
  `${file.name}-${file.size}-${file.lastModified}`;

const validateFile = (file: File): string | null => {
  const lower = file.name.toLowerCase();
  const okExt = /\.(png|jpe?g)$/.test(lower);
  const okMime =
    !file.type || ALLOWED_MIME.has(file.type) || file.type === "image/pjpeg";
  if (!okExt && !okMime) {
    return "PNG / JPG / JPEG のみアップロードできます。";
  }
  if (file.size > MAX_BYTES_PER_FILE) {
    return MSG_FILE_SIZE_EXCEEDED;
  }
  return null;
};

type EditorProps = {
  value?: unknown[];
  onChange?: (next: unknown[]) => void;
};

const AttachmentsEditor = (props: EditorProps) => {
  const { value = [], onChange } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const previewUrlsRef = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    return () => {
      previewUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      previewUrlsRef.current.clear();
    };
  }, []);

  const getPreviewForFile = useCallback((file: File) => {
    const key = filePreviewKey(file);
    const map = previewUrlsRef.current;
    if (!map.has(key)) {
      map.set(key, URL.createObjectURL(file));
    }
    return map.get(key)!;
  }, []);

  const revokeFilePreview = useCallback((file: File) => {
    const key = filePreviewKey(file);
    const map = previewUrlsRef.current;
    const u = map.get(key);
    if (u) {
      URL.revokeObjectURL(u);
      map.delete(key);
    }
  }, []);

  const handleRemove = (item: unknown, index: number) => {
    if (item instanceof File) {
      revokeFilePreview(item);
    }
    onChange?.(value.filter((_, i) => i !== index));
  };

  const onPickFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const list = input.files?.length ? Array.from(input.files) : [];
    input.value = "";
    if (!list.length) return;
    if (list.length > MAX_FILES_PER_PICK) {
      notification.error({ message: MSG_FILE_COUNT_EXCEEDED });
      return;
    }

    for (const file of list) {
      const err = validateFile(file);
      if (err) {
        notification.error({ message: err });
        return;
      }
    }
    onChange?.([...value, ...list]);
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_ATTR}
        multiple
        style={{ display: "none" }}
        onChange={onPickFiles}
      />
      {value.map((item, index) => {
        const src =
          item instanceof File ? getPreviewForFile(item) : resolveUrl(item);
        if (!src) return null;
        return (
          <div key={`${src}-${index}`} className="thumb-wrap">
            <Image
              src={src}
              alt=""
              width={38}
              height={38}
              style={{ objectFit: "cover", borderRadius: 6 }}
              preview
            />
            <button
              type="button"
              className="thumb-remove"
              aria-label="削除"
              onClick={() => handleRemove(item, index)}
            >
              <IoTrashOutline size={14} />
            </button>
          </div>
        );
      })}
      <button
        type="button"
        className="add-image-placeholder"
        onClick={() => inputRef.current?.click()}
      >
        <FileImageOutlined className="add-image-icon" />
        <PlusOutlined className="add-image-plus" />
        <span>画像選択</span>
      </button>
    </>
  );
};

const AttachmentsField = (props: IProps) => {
  const { logId, readonlyAttachments, editable = false } = props;

  const readonlyUrls = (readonlyAttachments || [])
    .map(resolveUrl)
    .filter((u): u is string => Boolean(u));

  if (!readonlyUrls.length && !editable) {
    return null;
  }

  if (!editable) {
    return (
      <Styles className="timeline-attachments">
        <IoCameraOutline size={20} />
        {readonlyUrls.map((src) => (
          <div key={src} className="thumb-wrap">
            <Image
              src={src}
              alt=""
              width={38}
              height={38}
              style={{ objectFit: "cover", borderRadius: 6 }}
              preview
            />
          </div>
        ))}
      </Styles>
    );
  }

  return (
    <Styles className="timeline-attachments">
      <IoCameraOutline size={20} />
      <Form.Item
        name={[logId, "attachments"]}
        style={{ marginBottom: 0, flex: 1, minWidth: 0 }}
        className="timeline-attachments-form-item"
      >
        <AttachmentsEditor />
      </Form.Item>
    </Styles>
  );
};

export default AttachmentsField;
